/**
 * Shared content data - single source of truth for marketing copy.
 * Used by both UI components and llms.txt generation.
 */

import { INDUSTRY_ROUTES, MARKETING_ROUTES, PRODUCT_NAME } from '@/common/constants';

type Stat = {
  value: number;
  prefix?: string;
  suffix: string;
};

/**
 * Format a stat as a display string (e.g., "+12.5%" or "2B+").
 */
export function formatStat(stat: Stat): string {
  return `${stat.prefix ?? ''}${stat.value}${stat.suffix}`;
}

export const SITE_NAME = 'Bazak';

export const SITE_TITLE = 'Bazak - The operating system for AI commerce';

export const SITE_DESCRIPTION =
  'Bazak turns your website and catalog into your AI agent - one agent, running on ChatGPT, Claude, and your own site. Live in 72 hours. No code changes.';

/**
 * Key statistics used across the site.
 */
export const STATS = {
  aiReferredLift: {
    value: 42,
    prefix: '+',
    suffix: '%',
  },
  aiUsers: {
    value: 2,
    suffix: 'B+',
  },
  messagesServed: {
    value: 500,
    suffix: 'K+',
  },
  sessionOutcomeRate: {
    value: 40,
    prefix: '>',
    suffix: '%',
  },
  revenueLift: {
    value: 12.5,
    prefix: '+',
    suffix: '%',
  },
  timeToLive: {
    value: 72,
    suffix: ' hours',
  },
  customersUsingAI: {
    value: 60,
    suffix: '%',
  },
} as const satisfies Record<string, Stat>;

/**
 * Key benefits/value propositions.
 * Each benefit has both a short title and descriptions for different contexts.
 */
export const KEY_BENEFITS = [
  {
    id: 'sell',
    title: 'Sell in the AI ecosystem',
    description:
      'A branded agent inside ChatGPT, Claude, Gemini, and MCP marketplaces. It quotes live inventory and prices, compares options, and books.',
    llmDescription: `Sell to ${formatStat(STATS.aiUsers)} people on ChatGPT, Claude, Gemini, and MCP marketplaces without ads`,
    stat: STATS.aiUsers,
  },
  {
    id: 'convert',
    title: 'Convert on your website',
    description:
      'The same agent closes on your own domain: intent, objections, cart, and handoff to your checkout.',
    llmDescription: `${formatStat(STATS.revenueLift)} on-site revenue lift with the same agent on your website`,
    stat: STATS.revenueLift,
  },
  {
    id: 'serve',
    title: 'Serve after the purchase',
    description:
      'Order status, date changes, cancellations, returns, and warranty claims from the agent that made the sale.',
    llmDescription:
      'Post-sale service on the same agent, with human handoff when policy requires it',
  },
  {
    id: 'engineering',
    title: 'Live in 72 hours',
    description:
      'No API access needed. Bazak reads your website and catalog and goes live in 72 hours. Zero code changes.',
    llmDescription: 'No API or code changes required. Live in 72 hours.',
    stat: STATS.timeToLive,
  },
] as const;

export const HERO_COPY = {
  titleLine1: 'Every brand and every customer',
  titleLine2: 'will interact through an agent.',
  subtitle:
    'Bazak turns your website and catalog into your AI operating system - one agent, with integrations running on ChatGPT, Claude, and your own site. Live in 72 hours. No code changes.',
  demoCta: 'Get a demo',
  howItWorksCta: 'See how it works',
} as const;

/** The assistants a Bazak agent reaches. Order drives the row of marks in the hero demo. */
export const AI_SURFACES = ['ChatGPT', 'Claude', 'Gemini', 'Perplexity'] as const;

export type AiSurface = (typeof AI_SURFACES)[number];

/** Assistants the step-1 demo can be shown inside. */
export const HERO_DEMO_SURFACES = ['ChatGPT', 'Claude'] as const;

export type HeroDemoSurface = (typeof HERO_DEMO_SURFACES)[number];

/**
 * The hero demo walks one customer through the same three stages JOURNEY_STAGES describes in
 * full - keep the two in step. This is the 30-second illustrated version; that section is the
 * explanation.
 */
export type HeroFunnelStage = {
  heading: string;
  /** Shown in place of the demo while the stage is waiting its turn. */
  idle: string;
  /** Closing line under the demo. Stage 2 opens with the handoff line instead. */
  footer?: string;
};

export const HERO_FUNNEL_STAGES: HeroFunnelStage[] = [
  {
    heading: 'Integration to the AI ecosystem',
    idle: 'Shoppers discover you inside ChatGPT and Claude',
  },
  {
    heading: 'Closing on your site with AI agents',
    idle: 'The agent closes the sale on your own domain',
  },
  {
    heading: 'Service, forever after',
    idle: 'It keeps serving the customer - on your brand site and across the AI ecosystem',
    footer: 'Same agent and in any agentic surface',
  },
];

export const HERO_FUNNEL_COPY = {
  label: 'Illustrative agent journey, by industry',
  surfaceToggle: 'Conversation is in',
  answeredInside: 'Your catalog, answered inside',
  handoff: 'Your agent picks up from',
  payment: ['Apple Pay', '•••• 4242'],
  receipt: ' · receipt sent',
  serviceTag: 'same agent · 24/7',
} as const;

export type HeroFunnelVertical = {
  /** Tab label, also the key we look the vertical up by. */
  tab: string;
  domain: string;
  /** Default assistant for this vertical; the step-1 toggle can override it. */
  ai: HeroDemoSurface;
  /** What the shopper asks the assistant. */
  ask: string;
  item: string;
  price: string;
  /** Call to action inside the assistant, then the one on the brand's own site. */
  aiCta: string;
  siteCta: string;
  confirmation: string;
  /** The post-purchase exchange with the same agent. */
  serviceAsk: string;
  serviceOk: string;
  image: string;
  imageLabel: string;
  serviceLabel: string;
};

export const HERO_FUNNEL_VERTICALS: HeroFunnelVertical[] = [
  {
    tab: 'Hotels',
    domain: 'yourhotel.com',
    ai: 'ChatGPT',
    ask: 'Oceanfront hotel in Miami, next weekend',
    item: 'Deluxe Ocean View King',
    price: '$389/night',
    aiCta: 'Book now',
    siteCta: 'Confirm & pay',
    confirmation: 'Booked · #4821',
    serviceAsk: 'Any chance of early check-in?',
    serviceOk: 'Room ready at 11:30 AM',
    image: '/hero/hotels.jpg',
    imageLabel: 'Miami · oceanfront',
    serviceLabel: 'Front desk · 24/7',
  },
  {
    tab: 'Flights',
    domain: 'yourairline.com',
    ai: 'Claude',
    ask: 'Nonstop JFK to Denver on Friday',
    item: 'JFK → DEN · 7:15 AM',
    price: '$189',
    aiCta: 'Book flight',
    siteCta: 'Pick seats & pay',
    confirmation: 'Ticket issued · 14A',
    serviceAsk: 'Move my return to Sunday',
    serviceOk: 'Rebooked · 8:40 PM',
    image: '/hero/flights.jpg',
    imageLabel: 'JFK → DEN · nonstop',
    serviceLabel: 'Trip changes · anytime',
  },
  {
    tab: 'Retail',
    domain: 'yourstore.com',
    ai: 'ChatGPT',
    ask: 'Laptop for video editing, under $2,000',
    item: '15" creator laptop',
    price: '$1,799',
    aiCta: 'Buy now',
    siteCta: 'Buy now',
    confirmation: 'Order #77120',
    serviceAsk: "Where's my order?",
    serviceOk: 'Out for delivery · by 2 PM',
    image: '/hero/retail.jpg',
    imageLabel: '15" creator laptop',
    serviceLabel: 'Order tracking · live',
  },
  {
    tab: 'Insurance',
    domain: 'yourinsurer.com',
    ai: 'Claude',
    ask: 'Business insurance quote for 12 employees',
    item: 'Business owner policy',
    price: '$410/mo',
    aiCta: 'Get covered',
    siteCta: 'Verify ID & activate',
    confirmation: 'Policy active',
    serviceAsk: 'Any update on my claim?',
    serviceOk: 'Approved · paid out Thursday',
    image: '/hero/insurance.jpg',
    imageLabel: 'Business coverage',
    serviceLabel: 'Claims · fast-track',
  },
  {
    tab: 'Finance',
    domain: 'yourbank.com',
    ai: 'ChatGPT',
    ask: 'Best rate on a $50k deposit?',
    item: '12-month fixed deposit · 4.8% APY',
    price: '$50,000',
    aiCta: 'Open deposit',
    siteCta: 'Fund deposit',
    confirmation: 'Deposit funded · $50k',
    serviceAsk: 'What happens at maturity?',
    serviceOk: 'Auto-renew set · rate locked',
    // A rate curve rather than a photo: no stock image says "4.8% APY on a deposit".
    image: '/hero/finance.svg',
    imageLabel: '$50,000 → $52,400 · 4.8% APY',
    serviceLabel: 'Deposit care · 24/7',
  },
];

export const SCANNER_COPY = {
  heading: 'Is your brand ready for the new internet of AI agents?',
  placeholder: 'yourbrand.com',
  button: 'Scan your brand',
  // No longer claims "no signup" — the scan asks for a work email before it runs.
  microcopyBefore: 'Free readiness scan. Or ',
  microcopyLink: 'get a demo',
  microcopyAfter: ' on your own catalog.',
  validationError: 'Enter your website address, like brand.com.',
  resultKicker: 'AGENT READINESS',
  simulatedLabel: 'SIMULATED',
  resultFooter:
    "The full findings, what we detected, what's missing, and what 72 hours closes, arrive by email.",
  emailLabel: 'Work email',
  emailPlaceholder: 'you@company.com',
  emailRequiredError: 'Email is required',
  emailInvalidError: 'Please enter a valid email address',
  emailModalTitle: 'Where should the report go?',
  emailModalDescriptionPrefix: 'We scan ',
  emailModalDescriptionSuffix: ' and send the full findings to your inbox.',
  emailModalSubmit: 'Start the scan',
  emailConsent:
    'We will use this email to send you the full report and might use it for one follow-up from our team.',
  emailSuccessPrefix: 'The full report is on its way to ',
  emailDemoCta: 'Get a demo on your own catalog',
} as const;

export const SCANNER_TIERS = {
  notReady: 'Not agent-ready',
  partial: 'Partially ready',
  nearly: 'Nearly ready',
} as const;

const PLATFORM_LEDE_BEFORE = 'ChatGPT, Claude, and Gemini put ';
const PLATFORM_LEDE_EMPHASIS = `${formatStat(STATS.aiUsers)} people one question away`;
const PLATFORM_LEDE_AFTER = ' from your offerings.';

export const PLATFORM_COPY = {
  kicker: '01  /  PLATFORM',
  title: 'The future belongs to AI Natives',
  titleAccent: 'AI Natives',
  ledeBefore: PLATFORM_LEDE_BEFORE,
  ledeEmphasis: PLATFORM_LEDE_EMPHASIS,
  ledeAfter: PLATFORM_LEDE_AFTER,
  lede: `${PLATFORM_LEDE_BEFORE}${PLATFORM_LEDE_EMPHASIS}${PLATFORM_LEDE_AFTER}`,
  bodyDetail:
    'Questions that went unanswered, objections that stalled a sale, answers that closed one - kept per brand, never shared, closing the gap next time.',
  brand: 'YOUR BRAND',
  brandItems: 'AI identity · Catalog & data · Pricing & decisioning rules',
  cycle: ['IMPROVE', 'DEPLOY', 'CONVERSE', 'LEARN'],
  os: 'BAZAK · THE OPERATING SYSTEM',
  osChipRows: [
    ['Guardrails', 'Evals'],
    ['Memory', 'Analytics'],
    ['MCP', 'ACP', 'UCP', 'X402'],
  ],
  osLines: ['GUARDRAILS · EVALS', 'MEMORY · ANALYTICS'],
  osProtocols: 'MCP · ACP · UCP · X402',
  osChips: [
    'Agent runtime',
    'Guardrails & evals',
    'Memory',
    'Analytics',
    'MCP',
    'ACP',
    'UCP',
    'x402',
  ],
  surfaces: ['Your website', 'ChatGPT', 'Claude', 'Branded App', 'WhatsApp', 'Voice Agents'],
  note: 'NO ECOSYSTEM LOCK-IN · TOTAL BRAND CONTROL',
} as const;

export const PLATFORM_SOURCES = [
  {
    label: 'McKinsey, Agentic Commerce, 2026',
    href: 'https://www.mckinsey.com/capabilities/quantumblack/our-insights/europes-agentic-commerce-moment-decision-influence-is-here-execution-is-coming',
  },
  {
    label: 'TechCrunch, AI assistant users, 2026',
    href: 'https://techcrunch.com/2026/06/16/chatgpts-market-share-slips-below-50-for-first-time/',
  },
  {
    label: 'Adobe Analytics via TechCrunch, 2026',
    href: 'https://techcrunch.com/2026/04/16/ai-traffic-to-us-retailers-rose-393-in-q1-and-its-boosting-their-revenue-too/',
  },
] as const;

export type PlatformStatTile = {
  value: number;
  prefix?: string;
  suffix: string;
  label: string;
};

export type PlatformStatGroup = {
  label: string;
  inverted: boolean;
  tiles: PlatformStatTile[];
};

export const PLATFORM_STAT_GROUPS: PlatformStatGroup[] = [
  {
    label: 'THE MARKET',
    inverted: false,
    tiles: [
      {
        value: STATS.aiReferredLift.value,
        prefix: STATS.aiReferredLift.prefix,
        suffix: STATS.aiReferredLift.suffix,
        label: 'AI-referred traffic converts better than any other channel',
      },
      {
        value: STATS.aiUsers.value,
        suffix: STATS.aiUsers.suffix,
        label: 'AI users reachable through assistant surfaces',
      },
    ],
  },
  {
    label: 'ON BAZAK',
    inverted: true,
    tiles: [
      {
        value: STATS.messagesServed.value,
        suffix: STATS.messagesServed.suffix,
        label: 'messages served by Bazak agents in production',
      },
      {
        value: STATS.sessionOutcomeRate.value,
        prefix: STATS.sessionOutcomeRate.prefix,
        suffix: STATS.sessionOutcomeRate.suffix,
        label: 'of sessions end in a completed outcome - a booking, a lead, or a resolved request',
      },
    ],
  },
];

export const JOURNEY_COPY = {
  kicker: '02  /  HOW IT WORKS',
  title: 'From first question to long after checkout',
  subtitle:
    'Sales and service run on the same agent with the same context, so your customers never repeat themselves.',
} as const;

export const JOURNEY_STAGES = [
  {
    n: '1',
    name: 'Sell',
    where: 'IN THE AI ECOSYSTEM',
    heading: 'Sell',
    body: 'Branded integration to the AI ecosystem that quotes your real inventory and sells for you.',
    bullets: [
      'Native ChatGPT Plugin, Claude Connector, and MCPs',
      'Live inventory, live prices',
      'New sales channels, zero ad spend',
    ],
  },
  {
    n: '2',
    name: 'Convert',
    where: 'ON YOUR WEBSITE',
    heading: 'Convert',
    body: 'Agent closes on your own domain: it knows the customer intent, answers objections, and hands off to your checkout.',
    bullets: [
      'Context keeps the conversation alive',
      'Proactively prevents churn',
      'Hands to your existing checkout',
    ],
  },
  {
    n: '3',
    name: 'Serve',
    where: 'AFTER THE PURCHASE',
    heading: 'Serve',
    body: 'Care from the agent that made the sale. One point of contact for changes, returns, and claims.',
    bullets: [
      'Self-serve changes that reduce support tickets',
      'Automated returns and claims',
      'Human handoff when policy requires it',
    ],
  },
] as const;

export const TIMELINE_COPY = {
  kicker: '03  /  TIMELINE',
  numeral: 72,
  unit: '72 HOURS · FIRST VALUE',
  title: 'Live before your next weekly meeting',
  titleAccent: 'your next weekly meeting',
  liveDow: 'THU',
  liveName: 'Live in production',
  liveBody: 'You walk into the meeting with your agent already answering customers.',
} as const;

export const TIMELINE_DAYS = [
  {
    n: '01',
    dow: 'MON · KICKOFF',
    name: 'Map',
    body: 'Bazak scans your stack and builds the integration. No code changes, no engineering ticket.',
  },
  {
    n: '02',
    dow: 'TUE',
    name: 'Prove',
    body: 'Guardrails, PII controls, and brand rules go in. Agent runs your eval suite until it passes.',
  },
  {
    n: '03',
    dow: 'WED',
    name: 'Ship',
    body: 'Live on your website and into the AI ecosystem, with conversation analytics from the first message.',
  },
] as const;

export const ENTERPRISE_COPY = {
  kicker: '04  /  SECURITY',
  title: 'Enterprise ready by design',
  titleAccent: 'by design',
  subtitle:
    'Everything your security, legal, and compliance teams will ask about, answered before they ask.',
  checklist: 'REVIEW CHECKLIST',
} as const;

export const ENTERPRISE_ROWS = [
  {
    term: 'Security & compliance',
    desc: 'PII blocking enforced at the platform layer before any model call. SOC 2 audit in progress.',
  },
  {
    term: 'Guardrails',
    desc: 'Price, policy, and claim guardrails with human handoff. Every answer traces back to a source in your catalog or policies.',
  },
  {
    term: 'Evaluation',
    desc: 'A per-brand eval suite gates go-live and reruns on every change, so behavior shifts are caught before customers see them.',
  },
  {
    term: 'Observability',
    desc: 'Conversation analytics across every surface. See what customers ask, what converts, and where deals stall.',
  },
  {
    term: 'Reliability',
    desc: 'SLAs, production monitoring, and a named customer success manager.',
  },
  {
    term: 'Data ownership',
    desc: 'Your data serves your agent only. No cross-tenant training, no resale, model-agnostic by contract.',
  },
] as const;

export const TESTIMONIALS_COPY = {
  kicker: '07  /  OPERATORS',
  title: 'What our clients say',
  // Named customer quotes are still in approval, so the section ships as placeholders.
  placeholder: 'COMING SOON',
  placeholderCount: 3,
} as const;

export const FINAL_CTA_COPY = {
  title:
    '"First movers train their agents on real customers. Everyone else starts from zero, later."',
  titleAccent: 'Everyone else starts from zero, later."',
  body: "We don't believe in slide decks. You will see how your brand works in production and decide for yourself.",
  demo: 'Get a demo',
  scan: 'Scan your brand',
} as const;

export const FOOTER_COPY = {
  protocols: 'SPEAKS MCP · ACP · AP2 · UCP · x402',
} as const;

/** Keyed by `StatusKey` from `@bazak/status`; the platform shows the same states via next-intl. */
export const STATUS_COPY = {
  operational: 'All systems operational',
  degraded: 'Degraded performance',
  down: 'Service disruption',
  unknown: 'Service status',
} as const;

export const SECTION_COPY = {
  news: {
    kicker: '05  /  CUSTOMER PRESS',
    title: 'Our customers make the headlines.',
    titleAccent: 'make the headlines.',
    subtitle: 'Every brand that launched with Bazak became the story in its category.',
  },
} as const;

export const SCAN_PAGE_COPY = {
  title: 'Is your brand ready for the new internet of AI agents?',
  titleAccent: 'the new internet of AI agents?',
  subtitle: 'The internet is going agentic. See where your brand stands in 30 seconds.',
  readyTitle: 'What ready means',
  dimensions: [
    {
      name: 'Sell',
      body: 'Can assistants find you, quote live prices, and book from your catalog?',
    },
    {
      name: 'Convert',
      body: 'When a buyer asks a question on your site, can an agent close?',
    },
    {
      name: 'Serve',
      body: 'Can customers change, cancel, or return an order without a ticket?',
    },
    {
      name: 'Rails',
      body: 'Are MCP, ACP, AP2, and x402 endpoints there for agents to use?',
    },
  ],
} as const;

export const INDUSTRIES_HUB_COPY = {
  title: 'When your market starts asking AI, who answers for your brand?',
  titleAccent: 'who answers for your brand?',
  // AX is not established outside this site, and to a retail or finance reader it otherwise
  // reads as Microsoft Dynamics AX, so the subtitle defines it on first use.
  subtitle:
    'Agentic experience (AX) is when an agent handles the conversation. Publicly traded brands already sell through Bazak agents in production.',
} as const;

export const NOT_FOUND_COPY = {
  title: "This page isn't ready for the agentic internet either.",
  titleAccent: 'the agentic internet either.',
  body: 'Scan your brand, or go back home.',
  home: 'Back home',
} as const;

export type TickerTag = 'SALE' | 'SERVICE';

export type TickerEvent = {
  tag: TickerTag;
  agent: string;
  event: string;
  where: string;
  logoSrc?: string;
};

export const TICKER_EVENTS: TickerEvent[] = [
  {
    tag: 'SALE',
    agent: 'Fattal',
    event: '3-night stay booked',
    where: 'in ChatGPT',
    logoSrc: '/logos/fattal.png',
  },
  {
    tag: 'SERVICE',
    agent: 'Fattal',
    event: 'guest pointed to self-serve booking change',
    where: 'on the hotel agent',
    logoSrc: '/logos/fattal.png',
  },
  {
    tag: 'SALE',
    agent: 'Israir',
    event: 'fare held on a direct route',
    where: 'in ChatGPT',
    logoSrc: '/logos/israir.svg',
  },
  {
    tag: 'SERVICE',
    agent: 'Israir',
    event: 'baggage policy question answered',
    where: 'on the airline agent',
    logoSrc: '/logos/israir.svg',
  },
  {
    tag: 'SALE',
    agent: 'ZAP',
    event: '14 offers compared, lead handed to seller',
    where: 'on the marketplace agent',
    logoSrc: '/logos/zap.svg',
  },
  {
    tag: 'SALE',
    agent: 'WallaShops',
    event: 'product question turned into a cart',
    where: 'on the shopping agent',
    logoSrc: '/logos/wallashops.svg',
  },
  {
    tag: 'SALE',
    agent: 'Ophir',
    event: 'vacation package booked',
    where: 'on the travel agent',
    logoSrc: '/logos/ophir.png',
  },
];

export type IndustryConvoLine =
  { kind: 'user'; text: string } | { kind: 'agent'; text: string } | { kind: 'tag'; text: string };

export type IndustryLink = {
  label: string;
  href: string;
};

export type Industry = {
  slug: string;
  href: string;
  label: string;
  tabLabel: string;
  verticalName: string;
  heading: string;
  claim: string;
  prooflines: { tag: string; text: string }[];
  links: IndustryLink[];
  metrics: string[];
  convoHeader: string;
  convo: IndustryConvoLine[];
};

export const INDUSTRIES: Industry[] = [
  {
    slug: 'travel-hospitality',
    href: INDUSTRY_ROUTES.travelHospitality,
    label: 'Travel & Hospitality',
    tabLabel: 'Travel',
    verticalName: 'travel and hospitality',
    heading: 'Bookings sold and managed inside the assistants',
    claim:
      'A guest asks for a beach weekend inside ChatGPT. The agent quotes live room inventory, holds the booking, and two weeks later moves the date, no call center, no support ticket.',
    prooflines: [
      { tag: 'SELL', text: 'Room and fare quotes from live inventory, in ChatGPT and Claude' },
      { tag: 'CONVERT', text: 'Booking completed on your own site, through your checkout' },
      { tag: 'SERVE', text: 'Date changes, cancellations, and upgrades in one message' },
    ],
    links: [],
    metrics: [
      `${formatStat(STATS.revenueLift)} ON-SITE REVENUE LIFT`,
      '50%+ OF SERVICE REQUESTS END IN SELF-SERVE',
    ],
    convoHeader: 'HOTEL AGENT · IN CHATGPT',
    convo: [
      { kind: 'user', text: 'Family suite by the beach this weekend, under $1,200 total.' },
      {
        kind: 'agent',
        text: 'Two match: one with breakfast at $1,040, one all-inclusive at $1,180. Want me to hold the first?',
      },
      { kind: 'tag', text: 'SERVICE · SAME AGENT, TWO WEEKS LATER' },
      { kind: 'user', text: 'Move my booking to December 14.' },
      { kind: 'agent', text: 'Done. New confirmation sent, no change fee.' },
    ],
  },
  {
    slug: 'retail-ecommerce',
    href: INDUSTRY_ROUTES.retailEcommerce,
    label: 'Retail & E-commerce',
    tabLabel: 'Retail',
    verticalName: 'retail and e-commerce',
    heading: 'Product questions that end in carts',
    claim:
      'A shopper describes what they need in plain words. The agent compares in-stock options, builds the cart, hands off to checkout, then opens the return when the color is wrong.',
    prooflines: [
      {
        tag: 'SELL',
        text: 'Catalog answers from live stock and prices, on-site and in the assistants',
      },
      { tag: 'CONVERT', text: 'Comparison to cart to your existing checkout' },
      { tag: 'SERVE', text: 'Returns opened, labels sent, refunds tracked' },
    ],
    links: [],
    metrics: [
      `${formatStat(STATS.revenueLift)} ON-SITE REVENUE LIFT`,
      '50%+ OF SERVICE REQUESTS END IN SELF-SERVE',
    ],
    convoHeader: 'RETAIL AGENT · ON-SITE',
    convo: [
      { kind: 'user', text: 'Quiet dishwasher for a small kitchen, under $900.' },
      {
        kind: 'agent',
        text: 'Three under 44 dB in stock. One fits an 18-inch slot and ships tomorrow. Compare all three, or add it to cart?',
      },
      { kind: 'tag', text: 'SERVICE · AFTER DELIVERY' },
      { kind: 'user', text: 'The color is wrong. I want to return it.' },
      {
        kind: 'agent',
        text: 'Return opened. Your prepaid label is in your inbox, refund lands 2 days after drop-off.',
      },
    ],
  },
  {
    slug: 'financial-services',
    href: INDUSTRY_ROUTES.financialServices,
    label: 'Financial Services',
    tabLabel: 'Financial',
    verticalName: 'financial services',
    heading: 'Regulated answers for investors',
    claim:
      'A customer screens funds inside Claude and gets answers with sources, not opinions. Compliance guardrails gate every reply, and existing clients ask about their own accounts in the same thread.',
    prooflines: [
      { tag: 'SELL', text: 'Fund search and screening in Claude and ChatGPT' },
      { tag: 'GATED', text: 'Every reply passes policy guardrails before it ships' },
      { tag: 'SERVE', text: 'Account and fee questions answered with a full audit trail' },
    ],
    links: [],
    metrics: ['BUILT FOR A REGULATED MARKET'],
    convoHeader: 'FUND AGENT · IN CLAUDE',
    convo: [
      { kind: 'user', text: 'Low-fee bond funds with a track record of 3+ years?' },
      {
        kind: 'agent',
        text: 'Four match. The lowest expense ratio is 0.29%. Want the fact sheets, or a side-by-side of all four?',
      },
      { kind: 'tag', text: 'SERVICE · EXISTING CLIENT' },
      { kind: 'user', text: 'What did I pay in fees last quarter?' },
      { kind: 'agent', text: '$184 across two funds. The breakdown is on its way to your inbox.' },
    ],
  },
  {
    slug: 'consumer-goods',
    href: INDUSTRY_ROUTES.consumerGoods,
    label: 'Consumer Goods',
    tabLabel: 'Consumer',
    verticalName: 'consumer goods',
    heading: 'From product question to replacement part',
    claim:
      'A buyer picks the right product from a global catalog in one conversation. Eight months later the same agent, with the same context, handles the warranty claim and ships the part.',
    prooflines: [
      { tag: 'SELL', text: 'Product selection across a global catalog' },
      { tag: 'SERVE', text: 'Warranty, parts, and care flows on the same agent' },
    ],
    links: [],
    metrics: ["ONE AGENT FOR THE PRODUCT'S WHOLE LIFE"],
    convoHeader: 'PRODUCT AGENT · ON-SITE',
    convo: [
      { kind: 'user', text: 'Storage bench that survives rain and seats two adults?' },
      {
        kind: 'agent',
        text: 'This one holds 70 gallons and seats two. Two colors, ships in 3 days.',
      },
      { kind: 'tag', text: 'SERVICE · EIGHT MONTHS LATER' },
      { kind: 'user', text: 'The lid hinge on mine snapped.' },
      {
        kind: 'agent',
        text: 'Covered by warranty. A replacement hinge ships free, claim link is ready for you.',
      },
    ],
  },
];

export const FOOTER_COLUMNS = [
  {
    title: 'PLATFORM',
    links: [
      { label: 'AI ecosystem distribution', href: MARKETING_ROUTES.howItWorks },
      { label: 'On-site agent', href: MARKETING_ROUTES.howItWorks },
      { label: 'Post-sale service', href: MARKETING_ROUTES.howItWorks },
      { label: 'Guardrails & evals', href: MARKETING_ROUTES.security },
      { label: 'Analytics', href: MARKETING_ROUTES.security },
    ],
  },
  {
    title: 'INDUSTRIES',
    links: INDUSTRIES.map(industry => ({ label: industry.label, href: industry.href })),
  },
  {
    title: 'COMPANY',
    links: [
      { label: 'News', href: MARKETING_ROUTES.news },
      { label: 'Pricing', href: MARKETING_ROUTES.pricing },
      { label: 'Get a demo', href: MARKETING_ROUTES.demo },
    ],
  },
] as const;

/**
 * Press coverage / "In the news" articles.
 * Used by both the news section UI and llms.txt generation.
 */
export type NewsArticle = {
  publication: string;
  title: string;
  href: string;
  /** Publication favicon, self-hosted under /public/news. */
  faviconSrc: string;
  // Some publications (Cloudflare-protected or otherwise) block Google
  // Translate's proxy, so we link straight to the original article for those.
  noTranslate?: boolean;
};

export const NEWS_ARTICLES: NewsArticle[] = [
  {
    publication: 'Israel Hayom',
    faviconSrc: '/news/israelhayom.png',
    title: 'Now ChatGPT Will Sell You Israir Flights Too',
    href: 'https://www.israelhayom.co.il/travel/aviation/article/20619836',
    noTranslate: true,
  },
  {
    publication: 'Passport News',
    faviconSrc: '/news/passportnews.png',
    title:
      'First in Israel and Among the First Worldwide: Israir Launches an AI Travel Agent Inside ChatGPT',
    href: 'https://passportnews.co.il/article/206179',
    noTranslate: true,
  },
  {
    publication: 'Bizportal',
    faviconSrc: '/news/bizportal.png',
    title: 'Israir Enters ChatGPT: Launches an AI Agent for Searching Flights and Vacations',
    href: 'https://www.bizportal.co.il/airlines/news/article/20032758',
    noTranslate: true,
  },
  {
    publication: 'IAS',
    faviconSrc: '/news/ias.png',
    title: 'The AI Revolution in Aviation: Israir Launches a Smart Travel Agent in ChatGPT',
    href: 'https://www.ias.co.il/%D7%AA%D7%A2%D7%95%D7%A4%D7%94/%D7%9E%D7%94%D7%A4%D7%9B%D7%AA-%D7%94-ai-%D7%91%D7%AA%D7%A2%D7%95%D7%A4%D7%94-%D7%99%D7%A9%D7%A8%D7%90%D7%99%D7%99%D7%A8-%D7%9E%D7%A9%D7%99%D7%A7%D7%94-%D7%A1%D7%95%D7%9B%D7%9F-%D7%A0%D7%A1%D7%99/',
  },
  {
    publication: 'Ynet',
    faviconSrc: '/news/ynet.png',
    title: "After Quicklizard's Exit: Israir Integrates a New AI Tool Into Its Booking System",
    href: 'https://www.ynet.co.il/capital/article/s1d9iomnwe',
  },
  {
    publication: 'Maariv',
    faviconSrc: '/news/maariv.png',
    title: 'Instead of Searching for a Hotel: Book a Vacation in a Single Conversation With AI',
    href: 'https://www.maariv.co.il/lifestyle/travel/article-1327999',
    noTranslate: true,
  },
  {
    publication: 'Passport News',
    faviconSrc: '/news/passportnews.png',
    title:
      'No Searching, No Comparing: The Veteran Hotel Chain Opens Up Vacation Booking Through ChatGPT',
    href: 'https://passportnews.co.il/article/206357',
    noTranslate: true,
  },
  {
    publication: 'Israeling',
    faviconSrc: '/news/israeling.png',
    title: "The AI Revolution in Tourism: Fattal Launches Israel's First AI Agent",
    href: 'https://israeling.co.il/vacay-tlv/hotels-tlv/fattal-hotels-launches-ai-travel-agent-chatgpt/',
  },
  {
    publication: 'IAS',
    faviconSrc: '/news/ias.png',
    title: 'Fattal Launches an Advanced AI Agent on Its Site and in ChatGPT',
    href: 'https://www.ias.co.il/%D7%9E%D7%9C%D7%95%D7%A0%D7%95%D7%AA/%D7%A8%D7%A9%D7%AA-%D7%A4%D7%AA%D7%90%D7%9C-%D7%9E%D7%A9%D7%99%D7%A7%D7%94-%D7%A1%D7%95%D7%9B%D7%9F-ai-%D7%9E%D7%AA%D7%A7%D7%93%D7%9D-%D7%91%D7%90%D7%AA%D7%A8-%D7%95%D7%91-chatgpt/',
  },
  {
    publication: 'Datilishes',
    faviconSrc: '/news/datilishes.png',
    title:
      'First in Israel: Fattal Hotels Launches an Advanced AI Agent on Its Site and in ChatGPT',
    href: 'https://datilishes.co.il/%D7%A8%D7%90%D7%A9%D7%95%D7%A0%D7%99%D7%9D-%D7%91%D7%99%D7%A9%D7%A8%D7%90%D7%9C-%D7%A8%D7%A9%D7%AA-%D7%9E%D7%9C%D7%95%D7%A0%D7%95%D7%AA-%D7%A4%D7%AA%D7%90%D7%9C-%D7%9E%D7%A9%D7%99%D7%A7%D7%94-%D7%A1/',
    noTranslate: true,
  },
  {
    publication: 'Israel Hayom',
    faviconSrc: '/news/israelhayom.png',
    title: "For the First Time in Israel: The Hotel Giant's Revolutionary Move",
    href: 'https://www.israelhayom.co.il/travel/travel-in-israel/article/20662008',
    noTranslate: true,
  },
];

/**
 * Positioning, audience, glossary, and FAQ. These began as prose hardcoded inside the llms.txt
 * route, which meant the definitions here and the same claims in the UI could drift apart. Anything
 * an LLM should be able to read about Bazak belongs here, not in the route.
 */

export const POSITIONING_COPY = {
  question: `What is ${PRODUCT_NAME}?`,
  body: `**${SITE_TITLE}.** It runs a brand's sales and service agents across the whole journey: discovery inside ChatGPT, Claude, and Gemini, conversion on the brand's own website, and care after checkout. Live in production in 72 hours. No API access and no code changes required.`,
} as const;

export const AUDIENCE_COPY =
  'CEOs, CMOs, and chief digital officers at brands in travel and hospitality, retail and e-commerce, financial services, and consumer goods. Publicly traded brands already sell through Bazak agents in production.';

/** Protocol and category definitions. The pricing and industries copy references the same terms. */
export const GLOSSARY = [
  {
    term: 'MCP (Model Context Protocol)',
    definition: 'Open protocol for connecting AI assistants to tools and data.',
  },
  {
    term: 'ACP (Agentic Commerce Protocol)',
    definition: 'Protocol for product discovery, comparison, and purchase by agents.',
  },
  {
    term: 'AP2 (Agent Payment Protocol 2)',
    definition: 'Protocol enabling agents to complete transactions.',
  },
  {
    term: 'UCP (Universal Commerce Protocol)',
    definition: 'Unified protocol for cross-platform AI commerce.',
  },
  {
    term: 'x402',
    definition:
      'Open HTTP-native payment protocol. Uses the HTTP 402 Payment Required status so agents can pay for APIs and resources with stablecoins, without accounts or API keys.',
  },
  {
    term: 'AX (Agentic Experience)',
    definition:
      'What CX becomes when an AI agent handles the conversation - discovery, purchase, and service in one thread.',
  },
  {
    term: 'Operating system for AI commerce',
    definition:
      "Bazak's category claim. One runtime for sales and service agents across every surface.",
  },
] as const;

export const FAQ_COPY = {
  kicker: '07  /  FAQ',
  title: 'Questions we get asked',
  titleAccent: 'we get asked',
  subtitle: 'What every brand wants to know before the first call.',
} as const;

/** Rendered on the homepage, emitted as FAQPage structured data, and listed in llms.txt. */
export const FAQ_ITEMS = [
  {
    question: 'Do I need to provide API access?',
    answer:
      'Bazak reads your existing website. API access is optional, no code changes, no engineering ticket required unless you need a custom operation that your website does not support.',
  },
  {
    question: 'How long does implementation take?',
    answer: '72 hours from kickoff to production for a first surface end to end.',
  },
  {
    question: 'What surfaces does the agent run on?',
    answer: 'ChatGPT, Claude, Gemini, MCP marketplaces, and your own website.',
  },
  {
    question: 'Does the same agent handle sales and service?',
    answer:
      'Yes. Context carries, so a customer who bought in ChatGPT can change the order on your website without repeating a word.',
  },
  {
    question: 'Is Bazak relevant to GEO?',
    answer:
      'Bazak is not a GEO company. GEO is about citations in generated answers. Bazak is the AI stack for real-time integration: live inventory, live prices, and operations connected to ChatGPT, Claude, Gemini, and your own site.',
  },
] as const;

/**
 * The flat proof list llms.txt publishes. Derived from the on-site stat tiles so the two can't
 * disagree, plus the two claims that have no tile of their own.
 */
export const PROOF_POINTS = [
  ...PLATFORM_STAT_GROUPS.flatMap(group =>
    group.tiles.map(tile => ({
      value: formatStat({ value: tile.value, prefix: tile.prefix, suffix: tile.suffix }),
      label: tile.label,
    })),
  ),
  { value: formatStat(STATS.revenueLift), label: 'on-site revenue lift' },
  { value: formatStat(STATS.timeToLive), label: 'from kickoff to production' },
];

/**
 * Technical features list for structured data.
 */
export const TECHNICAL_FEATURES = [
  'Sales and service agents across ChatGPT, Claude, Gemini, and your website',
  'Live inventory and pricing for AI assistants',
  'On-site conversion with handoff to existing checkout',
  'Post-sale service: changes, returns, warranty claims',
  'Open protocols: MCP, ACP, AP2, UCP, x402',
  'Conversation analytics across every surface',
  'Non-invasive onboarding: no API access, no code changes, live in 72 hours',
] as const;
